'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { FC, ReactNode, useRef } from 'react';
import Image from '@/components/Image';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import useDevice from '@/hooks/useDevice';
import { interpolateBlocks } from '@/lib/blocks-content';
import { buildEntrance, splitTitleLines } from '@/lib/lead-magnet/animation';
import { interpolateText } from '@/lib/lead-magnet/format';
import { resolveResponsiveMedia } from '@/lib/lead-magnet/scoring';
import { ResultTier } from '@/types/lead-magnet';
import css from './style.module.scss';
interface Props {
  tier: ResultTier;
  score: number;
  total: number;
  children?: ReactNode;
}
const LeadMagnetResult: FC<Props> = t0 => {
  const $ = _c(32);
  const {
    tier,
    score,
    total,
    children
  } = t0;
  const device = useDevice();
  const t1 = device === "mobile";
  let t2;
  if ($[0] !== t1 || $[1] !== tier.heroImage) {
    t2 = resolveResponsiveMedia(tier.heroImage, t1);
    $[0] = t1;
    $[1] = tier.heroImage;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const hero = t2;
  const t3 = total - score;
  let eyebrow;
  let t4;
  let title;
  if ($[3] !== score || $[4] !== t3 || $[5] !== tier.description || $[6] !== tier.eyebrow || $[7] !== tier.title || $[8] !== total) {
    const tokens = {
      score,
      total,
      passed: score,
      failed: t3
    };
    eyebrow = tier.eyebrow ? interpolateText(tier.eyebrow, tokens) : null;
    title = interpolateBlocks(tier.title, tokens);
    t4 = interpolateBlocks(tier.description, tokens);
    $[3] = score;
    $[4] = t3;
    $[5] = tier.description;
    $[6] = tier.eyebrow;
    $[7] = tier.title;
    $[8] = total;
    $[9] = eyebrow;
    $[10] = t4;
    $[11] = title;
  } else {
    eyebrow = $[9];
    t4 = $[10];
    title = $[11];
  }
  const description = t4;
  const rootRef = useRef(null);
  let t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const root = rootRef.current;
      if (!root) {
        return;
      }
      const titleSplit = splitTitleLines(root, css.title);
      const timeline = buildEntrance([{
        targets: root.querySelector(`.${css.heroImage}`),
        role: "image",
        position: 0,
        duration: 1.3,
        vars: {
          ease: "power2.out"
        }
      }, {
        targets: titleSplit.lines,
        role: "title",
        position: 0.1,
        duration: 1,
        stagger: 0.13
      }, {
        targets: root.querySelector(`.${css.eyebrow}`),
        position: 0.22,
        duration: 0.6
      }, {
        targets: root.querySelector(`.${css.description}`),
        position: 0.48,
        duration: 0.8
      }, {
        targets: root.querySelector("form"),
        role: "control",
        position: 0.66,
        duration: 0.65
      }]);
      timeline.eventCallback("onComplete", titleSplit.revert);
      return titleSplit.revert;
    };
    t6 = {
      scope: rootRef
    };
    $[12] = t5;
    $[13] = t6;
  } else {
    t5 = $[12];
    t6 = $[13];
  }
  useGSAP(t5, t6);
  let t7;
  if ($[14] !== hero) {
    t7 = hero ? <Image loading="eager" className={css.heroImage} src={hero.url} alt={hero.alt} width={hero.width ?? 1920} height={hero.height ?? 1080} sizes="100vw" /> : null;
    $[14] = hero;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <div className={css.backdrop}>{t7}</div>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== eyebrow) {
    t9 = eyebrow ? <Typography uppercase={true} variant="body-m" tag="p" className={css.eyebrow}>{eyebrow}</Typography> : null;
    $[18] = eyebrow;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== title) {
    t10 = title ? <Typography uppercase={true} variant="h1" tag="h1" className={css.title}><StrapiRichContent content={title} /></Typography> : null;
    $[20] = title;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== description) {
    t11 = description ? <Typography variant="body-m" tag="div" className={css.description}><StrapiRichContent content={description} /></Typography> : null;
    $[22] = description;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== children || $[25] !== t10 || $[26] !== t11 || $[27] !== t9) {
    t12 = <div className={css.content}>{t9}{t10}{t11}{children}</div>;
    $[24] = children;
    $[25] = t10;
    $[26] = t11;
    $[27] = t9;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t8) {
    t13 = <section ref={rootRef} className={css.result}>{t8}{t12}</section>;
    $[29] = t12;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
};
export default LeadMagnetResult;